import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@core/store/store.ts";

const STORAGE_KEY = "complaintCustomOptions";

export interface CustomOptionsComplaintState {
  incidentLocations: string[];
}

const normalizeText = (s: string): string =>
  s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const loadFromStorage = (): CustomOptionsComplaintState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as CustomOptionsComplaintState;
  } catch {
    // ignore parse errors
  }
  return { incidentLocations: [] };
};

const saveToStorage = (state: CustomOptionsComplaintState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore write errors
  }
};

const initialState: CustomOptionsComplaintState = loadFromStorage();

const customOptionsComplaintSlice = createSlice({
  name: "customOptionsComplaint",
  initialState,
  reducers: {
    /**
     * Add a custom "other" incident location typed by the user.
     * Duplicate detection is case-insensitive and accent-insensitive.
     * The value is persisted to localStorage on every addition.
     */
    addCustomIncidentLocation: (state, action: PayloadAction<string>) => {
      const label = action.payload.trim();
      if (!label) return;

      const alreadyExists = state.incidentLocations.some(
        (loc) => normalizeText(loc) === normalizeText(label)
      );

      if (!alreadyExists) {
        state.incidentLocations.push(label);
        saveToStorage({ incidentLocations: [...state.incidentLocations] });
      }
    },
  },
});

export const { addCustomIncidentLocation } = customOptionsComplaintSlice.actions;

export const customComplaintOptions = (
  state: RootState
): CustomOptionsComplaintState => state.customOptionsComplaint;

export default customOptionsComplaintSlice.reducer;
